import {
  NotificationQueryParams,
  NotificationCenterContextType,
} from "@/modules/notification-center/dtos";
import { createContext, useContext, useState } from "react";
import { formatDateSafely } from "@/modules/common/components/ui/date-picker/date-range-picker";
import { useNotificationsList } from "@/modules/notification-center/hooks/notifications-list.hook";

const NotificationCenterContext = createContext<NotificationCenterContextType>({
  notifications: [],
  isNotificationsLoading: false,
  hasNextPage: true,
  unreadCount: 0,
  fetchNextPage: () => Promise.resolve(),

  params: {
    page: 1,
    pageSize: 10,
    read: null,
    createdAtMin: null,
    createdAtMax: null,
    systemTemplateId: null,
  },

  setParams: () => {},
  setDateRange: () => {},
  setReadStatusFilter: () => {},
  handlePageChange: () => {},
  setSelectedNotificationType: () => {},
  selectedNotificationType: null,
  selectedDateRangeString: null,
});

function useNotificationCenterContext() {
  const context = useContext(NotificationCenterContext);
  if (!context) {
    throw new Error(
      "useNotificationCenterContext must be used within a NotificationCenterProvider"
    );
  }
  return context;
}

function NotificationCenterProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [params, setParamsState] = useState<NotificationQueryParams>({
    page: 1,
    pageSize: 10,
    systemTemplateId: null,
    read: null,
    createdAtMin: null,
    createdAtMax: null,
  });

  const [selectedNotificationType, setSelectedNotificationTypeState] = useState<
    string | null
  >(null);

  const [selectedDateRangeString, setSelectedDateRangeStringState] = useState<
    string | null
  >(null);

  const { notifications, hasNextPage, isNotificationsLoading, fetchNextPage } =
    useNotificationsList({ params: params });
  const { count: unreadCount } = useNotificationsList({
    params: { ...params, read: false } as NotificationQueryParams,
  });

  const setParams = (newParams: Partial<NotificationQueryParams>) => {
    setParamsState((prevParams) => ({
      ...prevParams,
      ...newParams,
    }));
  };

  const handlePageChange = (page: number) => {
    setParams({ page });
  };

  const setSelectedNotificationType = (systemTemplateId: string | null) => {
    setSelectedNotificationTypeState(systemTemplateId);
    setParams({ page: 1, systemTemplateId });
  };

  const setReadStatusFilter = (read: boolean | null) => {
    setParams({ page: 1, read });
  };

  const setDateRange = (from: string | null, to: string | null) => {
    const fromDate = from ? new Date(from) : null;
    const toDate = to ? new Date(to) : null;

    if (fromDate && toDate) {
      setSelectedDateRangeStringState(
        `${formatDateSafely(fromDate)} - ${formatDateSafely(toDate)}`
      );
    } else {
      setSelectedDateRangeStringState("");
    }

    setParams({
      page: 1,
      createdAtMin: from,
      createdAtMax: to,
    });
  };

  return (
    <NotificationCenterContext.Provider
      value={{
        notifications,
        isNotificationsLoading,
        hasNextPage,
        fetchNextPage,
        params,
        setParams,
        handlePageChange,
        setSelectedNotificationType,
        selectedNotificationType,
        selectedDateRangeString,
        setReadStatusFilter,
        setDateRange,
        unreadCount,
      }}
    >
      {children}
    </NotificationCenterContext.Provider>
  );
}

export { useNotificationCenterContext, NotificationCenterProvider };
